import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatCurrency = (value) => {
  if (value == null || value === '') return '';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
};

/**
 * Generate and download an invoice PDF from the line-items API response.
 * @param {Object} lineItemsResponse - Response from GET Invoice/line-items/{remoteID}: { items: Array, currentPage, pagesCount, totalRecords }
 * @param {string} [docNumber] - Invoice doc number from the invoice table (shown as Invoice # and used in filename)
 */
export const generateInvoicePdf = (lineItemsResponse, docNumber) => {
  const items = lineItemsResponse?.items ?? [];
  if (items.length === 0) return;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const rightMargin = 14;
  const invoiceLabel = docNumber != null && String(docNumber).trim() !== ''
    ? String(docNumber).trim()
    : (items[0]?.invoiceID ?? items[0]?.invoiceId ?? 'Invoice');
  let y = 15;

  doc.setFontSize(18);
  const invoiceTitle = 'Invoice';
  doc.text(invoiceTitle, pageWidth - rightMargin - doc.getTextWidth(invoiceTitle), y);
  y += 8;

  doc.setFontSize(11);
  const invoiceNumberText = `Invoice #${invoiceLabel}`;
  doc.text(invoiceNumberText, pageWidth - rightMargin - doc.getTextWidth(invoiceNumberText), y);
  y += 10;

  const first = items[0];
  if (first) {
    const billLines = [first.billAddrLine1, first.billAddrLine2, first.billAddrLine3].filter(Boolean);
    const shipLines = [first.shipAddrLine1, first.shipAddrLine2, first.shipAddrLine3].filter(Boolean);

    if (billLines.length > 0) {
      doc.setFont(undefined, 'bold');
      doc.text('Bill To', 14, y);
      doc.setFont(undefined, 'normal');
      y += 6;
      billLines.forEach((line) => {
        doc.text(line, 14, y);
        y += 5;
      });
      y += 4;
    }

    if (shipLines.length > 0) {
      doc.setFont(undefined, 'bold');
      doc.text('Ship To', 14, y);
      doc.setFont(undefined, 'normal');
      y += 6;
      shipLines.forEach((line) => {
        doc.text(line, 14, y);
        y += 5;
      });
      y += 8;
    }
  }

  const tableData = items.map((row) => {
    const qtyValue = Number(row.qty);
    return [
      row.lineNum != null ? String(row.lineNum) : '',
      row.name != null ? String(row.name) : '',
      Number.isFinite(qtyValue) ? String(qtyValue) : '',
      formatCurrency(row.unitPrice),
      formatCurrency(row.amount),
    ];
  });
  const totalAmount = items.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

  autoTable(doc, {
    startY: y,
    head: [['Line #', 'Name', 'Qty', 'Unit Price($)', 'Amount($)']],
    body: tableData,
    foot: [['', '', '', '', `Total: $${formatCurrency(totalAmount)}`]],
    showFoot: 'lastPage',
    theme: 'grid',
    headStyles: {fillColor: [66, 139, 202]},
    footStyles: {
      fontStyle: 'bold',
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineWidth: {top: 0.1, right: 0, bottom: 0, left: 0},
      lineColor: [200, 200, 200],
    },
    columnStyles: {
      3: {halign: 'right'},
      4: {halign: 'right'},
    },
    didParseCell: (data) => {
      if (data.section === 'foot' && data.column.index === 4) {
        data.cell.styles.halign = 'right';
      }
    },
    margin: {left: 14},
  });

  doc.save(`invoice-${invoiceLabel}.pdf`);
};
